import { Effect } from 'effect';
import { useState, useEffect, useMemo, useCallback } from 'react';
import { sessionStatusEffect } from '../client';
import { useRequestOwner } from './use-request-owner';
import type { OkraSession, DocumentStatus } from '../types';
import { okraHookFailure, runOkraHookEffect, type OkraHookError } from './hook-io';
import { useOkra } from './provider';

export interface UseDocumentStatusOptions {
  /** Poll interval in ms while processing (default: 2000). 0 to disable. */
  pollInterval?: number;
  /** Skip fetching entirely */
  enabled?: boolean;
}

export interface UseDocumentStatusReturn {
  data: DocumentStatus | null;
  isLoading: boolean;
  error: Error | null;
  isComplete: boolean;
  isProcessing: boolean;
  refetch: () => void;
}

const COMPLETE_PHASES = new Set(['complete', 'awaiting_review']);
const PROCESSING_PHASES = new Set(['uploading', 'parsing', 'hydrating', 'verifying']);

/**
 * Native status program — the IO seam behind {@link useDocumentStatus}.
 *
 * One poll tick. Retry/interval scheduling stays in the hook so the React
 * lifecycle (and its cleanup) keeps owning cancellation.
 */
export function fetchDocumentStatusEffect(
  session: OkraSession,
  signal?: AbortSignal,
): Effect.Effect<DocumentStatus, OkraHookError> {
  return sessionStatusEffect(session, signal).pipe(
    Effect.mapError((error) =>
      okraHookFailure('documentStatus', 'Document status could not be fetched', error.cause),
    ),
  );
}

/**
 * Poll document processing status from the CF Worker.
 * Accepts either a session object or a raw document ID.
 *
 * ```tsx
 * const { data, isComplete, isProcessing } = useDocumentStatus(session);
 * // or
 * const { data } = useDocumentStatus('doc-abc123');
 * ```
 */
export function useDocumentStatus(
  sessionOrId: OkraSession | string | null,
  options: UseDocumentStatusOptions = {},
): UseDocumentStatusReturn {
  const { pollInterval = 2000, enabled = true } = options;
  const { client } = useOkra();

  const [data, setData] = useState<DocumentStatus | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Resolve to session object
  const session = useMemo(
    () => (typeof sessionOrId === 'string' ? client.sessions.from(sessionOrId) : sessionOrId),
    [client, sessionOrId],
  );
  const owner = useRequestOwner(session, enabled);

  const fetchStatus = useCallback(async () => {
    if (!session || !enabled) return;
    const ac = owner.begin();
    if (!ac) return;
    try {
      setIsLoading(true);
      const s = await runOkraHookEffect(fetchDocumentStatusEffect(session), ac.signal);
      if (!owner.owns(ac)) return;
      setData(s);
      setError(null);
    } catch (err) {
      if (!owner.owns(ac) || (err as Error).name === 'AbortError') return;
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      if (owner.owns(ac)) setIsLoading(false);
    }
  }, [session, enabled, owner]);

  // Reset when the selected session changes
  useEffect(() => {
    setData(null);
    setError(null);
  }, [session]);

  // Initial fetch
  useEffect(() => {
    setIsLoading(false);
    void fetchStatus();
  }, [fetchStatus]);

  // Poll while processing
  useEffect(() => {
    if (!session || !pollInterval || !enabled) return;
    if (data && COMPLETE_PHASES.has(data.phase)) return;

    const interval = setInterval(fetchStatus, pollInterval);
    return () => clearInterval(interval);
  }, [session, pollInterval, enabled, data?.phase, fetchStatus]);

  return {
    data,
    isLoading,
    error,
    isComplete: data ? COMPLETE_PHASES.has(data.phase) : false,
    isProcessing: data ? PROCESSING_PHASES.has(data.phase) : false,
    refetch: fetchStatus,
  };
}
